import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import InfoIcon from '@mui/icons-material/Info';

import { EntityActivityLogDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, SimpleFormatNumber } from 'src/utils/utility';

import StatusChip from 'src/presentations/components/organisms/dashboard/StatusChip';
import IconDocumentText from 'src/presentations/components/atom/icons/DocumentText';
import IconBank from 'src/presentations/components/atom/icons/Bank';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconWarning from 'src/presentations/components/atom/icons/Warning';
import { OPERATION_ROLE } from 'src/config';
import { format } from 'date-fns';

/**
 * getLogIconStyles
 */
export const getLogIconStyles = (log: EntityActivityLogDto) => {
  const { activityType, objectStatus, actionOnObject } = log;
  let styles = 'w-36 h-36 rounded-5 flex items-center justify-center';
  if (
    (activityType === 'DISPUTE_UPDATE' && actionOnObject === 'DISPUTE_STARTED') ||
    (activityType === 'CREDIT_NOTE_UPDATE' && actionOnObject === 'CREDIT_NOTE_DELETED') ||
    activityType === 'DEVIATION_UPDATE'
  ) {
    styles += ' bg-[#FCE4E9]';
  } else if (activityType === 'DISPUTE_UPDATE' && actionOnObject === 'DISPUTE_RESOLVED') {
    styles += ' bg-[#CCEBD5]';
  } else if (activityType === 'NO_CONTRACT_FOUND_UPDATE' && actionOnObject === 'REJECTED') {
    styles += ' bg-[#CCEBD5]';
  } else if (activityType === 'STATUS_UPDATE' && actionOnObject === 'REJECTED') {
    styles += ' bg-[#CCEBD5]';
  } else if (activityType === 'NO_CONTRACT_FOUND_UPDATE' && objectStatus === 'OBJECT_NO_CONTRACT_FOUND') {
    styles += ' bg-[#FDEAD3]';
  } else if (activityType === 'CREDIT_NOTE_UPDATE' && actionOnObject === 'CREDIT_NOTE_UPLOADED') {
    styles += ' bg-[#D4ECF5]';
  } else if (activityType === 'DOCUMENT_UPDATE' && actionOnObject === 'DOCUMENT_UPLOADED') {
    styles += ' bg-[#D4ECF5]';
  } else if (activityType === 'DOCUMENT_UPDATE' && actionOnObject === 'DOCUMENT_DELETED') {
    styles += ' bg-[#FCE4E9]';
  } else {
    switch (objectStatus) {
      case 'OBJECT_APPROVED':
        styles += ' bg-[#CCEBD5]';
        break;

      case 'OBJECT_FLAGGED':
      case 'OBJECT_TO_REJECT':
      case 'OBJECT_TO_APPROVE':
      case 'OBJECT_PENDING_DOCUMENT':
        styles += ' bg-[#FDEAD3]';
        break;

      case 'OBJECT_UPLOADED':
        styles += ' bg-[#D4ECF5]';
        break;

      case 'OBJECT_REJECTED':
        styles += ' bg-[#FCE4E9]';
        break;
      default:
        styles += ' bg-[#CCEBD5]';
        break;
    }
  }

  return styles;
};

/**
 * getLogIcon
 */
export const getLogIcon = (log: EntityActivityLogDto) => {
  const { activityType, objectStatus, actionOnObject } = log;

  if (activityType === 'DISPUTE_UPDATE' && actionOnObject === 'DISPUTE_STARTED') {
    return <IconBank />;
  }
  if (activityType === 'DISPUTE_UPDATE' && actionOnObject === 'DISPUTE_RESOLVED') {
    return <IconBank color='#00992B' />;
  }
  if (activityType === 'DISPUTE_UPDATE' && actionOnObject === 'DISPUTE_CANCELED') {
    return <IconBank color='#F79824' />;
  }
  if (activityType === 'CREDIT_NOTE_UPDATE' && actionOnObject === 'CREDIT_NOTE_DELETED') {
    return <IconTrash />;
  }
  if (activityType === 'CREDIT_NOTE_UPDATE' && actionOnObject === 'CREDIT_NOTE_UPLOADED') {
    return <IconDocumentText />;
  }
  if (activityType === 'DOCUMENT_UPDATE' && actionOnObject === 'DOCUMENT_DELETED') {
    return <IconTrash />;
  }
  if (activityType === 'DOCUMENT_UPDATE') {
    return <IconDocumentText />;
  }
  if (activityType === 'NO_CONTRACT_FOUND_UPDATE' && actionOnObject === 'REJECTED') {
    return <CheckCircleIcon sx={{ color: '#00992B', fontSize: 20 }} />;
  }
  if (activityType === 'STATUS_UPDATE' && actionOnObject === 'REJECTED') {
    return <CheckCircleIcon sx={{ color: '#00992B', fontSize: 20 }} />;
  }
  if (activityType === 'DEVIATION_UPDATE') {
    return <IconWarning />;
  }

  switch (objectStatus) {
    case 'OBJECT_APPROVED':
      return <CheckCircleIcon sx={{ color: '#00992B', opacity: 0.8, fontSize: 20 }} />;

    case 'OBJECT_FLAGGED':
    case 'OBJECT_TO_REJECT':
    case 'OBJECT_TO_APPROVE':
      return <InfoIcon sx={{ color: '#F79824', fontSize: 20 }} />;

    case 'OBJECT_UPLOADED':
      return <IconDocumentText />;

    case 'OBJECT_REJECTED':
      return <ReportIcon sx={{ color: '#E72547', fontSize: 20 }} />;
    default:
      return <InfoIcon sx={{ color: '#F79824', fontSize: 20 }} />;
  }
};

/**
 * getLogTitle
 */
export const getLogTitle = (log: EntityActivityLogDto) => {
  const { activityType, actionOnObject } = log;

  switch (activityType) {
    case 'STATUS_UPDATE':
      return 'Status Update';

    case 'DOCUMENT_UPDATE':
      return 'Document Update';

    case 'INVOICE_UPLOAD':
      return 'Invoice Uploaded';
    case 'NO_CONTRACT_FOUND_UPDATE':
      return 'Status Update';
    case 'CREDIT_NOTE_UPDATE': {
      if (actionOnObject === 'CREDIT_NOTE_UPLOADED') {
        return 'Credit Note Uploaded';
      }
      if (actionOnObject === 'CREDIT_NOTE_DELETED') {
        return 'Credit Note Deleted';
      }
      return 'Credit Note Uploaded';
    }

    case 'DEVIATION_UPDATE':
      return 'Deviation Update';

    case 'DISPUTE_UPDATE': {
      if (actionOnObject === 'DISPUTE_STARTED') {
        return 'Dispute Started';
      }
      if (actionOnObject === 'DISPUTE_RESOLVED') {
        return 'Dispute Resolved';
      }
      if (actionOnObject === 'DISPUTE_CANCELED') {
        return 'Dispute Canceled';
      }
      return 'Dispute Update';
    }
    default:
      return 'Status Update';
  }
};

/**
 * getLogInfo
 */
export const getLogInfo = (log: EntityActivityLogDto) => {
  const { activityType, actionOnObject, objectStatus } = log;

  switch (activityType) {
    case 'INVOICE_UPLOAD':
    case 'CREDIT_NOTE_UPDATE':
      return `by ${log?.actionBy}`;
    case 'DOCUMENT_UPDATE': {
      const actionOnObjectBeautified = beautifyEnum(log?.actionOnObject || '');
      const statusInfo = `${actionOnObjectBeautified} by ${log?.actionBy}`;
      return statusInfo;
    }

    case 'DISPUTE_UPDATE': {
      if (actionOnObject === 'DISPUTE_RESOLVED') {
        const disputeName = log?.disputeAgreementType?.toLowerCase()?.replace(/_/g, ' ');
        return `${disputeName} by ${log?.actionBy}`;
      }
      return `By ${log?.actionBy}`;
    }

    // Status Update
    case 'STATUS_UPDATE': {
      const chipStatusName = objectStatus?.replace('OBJECT_', '');
      let statusInfo = `From ${log?.actionBy}`;

      if (actionOnObject === 'SENT_TO_OPERATION') {
        statusInfo += ` to ${OPERATION_ROLE}`;
      } else if (actionOnObject === 'SENT_TO_PROCUREMENT') {
        statusInfo += ` to Procurement`;
      } else if (actionOnObject === 'SENT_TO_FINANCE') {
        statusInfo += ` to Finance`;
      } else if (actionOnObject === 'MARKED_TO_APPROVE') {
        statusInfo = `By ${log?.actionBy}`;
      }

      // Check for emailDeliveryHistory and add beautified final resolution
      const beautifiedFinalResolution = log?.finalResolution
        ?.toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

      const emailMessage = log?.emailDeliveryHistory ? ' - Email has been sent out' : '';

      return (
        <>
          <div>{statusInfo}</div>
          {beautifiedFinalResolution && (
            <div>
              {beautifiedFinalResolution}
              {emailMessage}
            </div>
          )}
          <div className='mt-6'>
            <StatusChip status={chipStatusName} size='small' />
          </div>
        </>
      );
    }
    // NRCF
    case 'NO_CONTRACT_FOUND_UPDATE': {
      const chipStatusName = objectStatus?.replace('OBJECT_', '');
      let statusInfo = `By ${log?.actionBy}`;

      if (actionOnObject === 'SENT_TO_OPERATION') {
        statusInfo += ` to ${OPERATION_ROLE}`;
      } else if (actionOnObject === 'SENT_TO_PROCUREMENT') {
        statusInfo += ` to Procurement`;
      } else if (actionOnObject === 'SENT_TO_FINANCE') {
        statusInfo += ` to Finance`;
      } else if (actionOnObject === 'MARKED_TO_APPROVE') {
        statusInfo = `By ${log?.actionBy}`;
      } else if (actionOnObject === 'REJECTED') {
        const beautifiedFinalResolution = log?.finalResolution
          ?.toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase()); // Beautify final resolution

        const chipStatusName = objectStatus?.replace('OBJECT_', ''); // Get chip status
        const emailMessage = log?.emailDeliveryHistory ? ' - Email has been sent out' : '';

        return (
          <>
            {/* Display actionBy with a line break */}
            <div>By {log?.actionBy}</div>

            {/* Display final resolution and email information on a new line */}
            <div>
              {beautifiedFinalResolution}
              {emailMessage}
            </div>

            {/* Display StatusChip */}
            <div className='mt-6'>
              <StatusChip status={chipStatusName} size='small' />
            </div>
          </>
        );
      }

      return (
        <>
          <div>{statusInfo}</div>
          <div className='mt-6'>
            <StatusChip status={chipStatusName} size='small' />
            {objectStatus === 'OBJECT_NO_CONTRACT_FOUND' && <span className='text-12 ml-5'>No rate card found</span>}
          </div>
        </>
      );
    }

    //  DEVIATION WITH TYPE
    case 'DEVIATION_UPDATE': {
      if (!log?.deviationActivity) {
        return <div>No deviation</div>;
      }
      let label = '';
      switch (log?.deviationActivity?.deviationType) {
        case 'UNIT_PRICE':
          label = 'New Unit Price';
          break;
        case 'NEW_CHARGE':
          label = 'Unknown Charge';
          break;
        case 'QUANTITY':
          label = 'New Quantity';
          break;
        case 'DOUBLE_CHARGE':
          label = 'Double Charge';
          break;
        default:
          label = '';
      }

      return (
        <>
          <div>{label}</div>
          <div className='mt-1'>
            {log?.deviationActivity?.chargeName} {SimpleFormatNumber(log?.deviationActivity?.unitPrice, log?.deviationActivity?.chargesCurrency === 'JPY')}{' '}
            {log?.deviationActivity?.chargesCurrency}
          </div>
          <div className='mt-1'>
            From {format(new Date(log?.deviationActivity?.startDate), 'dd-MMM-yyyy')} to {format(new Date(log?.deviationActivity?.endDate), 'dd-MMM-yyyy')}
          </div>
        </>
      );
    }

    default:
      return '';
  }
};
